<template>
    <slide-panel :visibility="visibility" @close="handleBackButton">
        <transition name="component-fade" mode="out-in">
            <g-loader v-if="isPanelLoading || !isVisible" type="fixed" />
            <panel-category-items
                v-else-if="categoryListVisibility"
                :brief="brief"
                :categories="categoryList"
                @selected="toCategory"
            />
            <panel-product-items
                v-else-if="productListVisibility"
                :brief="brief"
                :products="productsList"
                @selected="toSingle"
            >
                <template #footer>
                    <g-loader v-if="isLoading" class="products-load-more-spinner" />
                    <g-button
                        v-else-if="showLoadMore"
                        shape="round"
                        theme="live"
                        size="xlarge"
                        block
                        style="margin: 10px 0"
                        @click="loadMoreProducts"
                    >
                        Load More
                    </g-button>
                </template>
            </panel-product-items>
            <new-single-product-panel-item
                v-else-if="singleItemVisibility && useNewVariantsSelection"
                :brief="brief"
                :update-mode="!!editingProduct"
                :panel-slot="panelSlot"
                :existing-values="(currentSelection && currentSelection.options) || currentOptions"
                :product="selectedItem"
                class="item"
                @updated="$emit('updated', $event)"
                @added="$emit('added', $event)"
            />
            <single-product-panel-item
                v-else-if="singleItemVisibility && !useNewVariantsSelection"
                :brief="brief"
                :update-mode="!!editingProduct"
                :panel-slot="panelSlot"
                :existing-values="(currentSelection && currentSelection.options) || currentOptions"
                :product="selectedItem"
                class="item"
                @updated="$emit('updated', $event)"
                @added="$emit('added', $event)"
            />
        </transition>
    </slide-panel>
</template>

<script>
    import { get, isEmpty } from 'lodash';
    import SingleProductPanelItem from './SingleProductPanelItem.vue';
    import NewSingleProductPanelItem from './NewSingleProductPanelItem.vue';
    import PanelProductItems from './PanelProductItems.vue';
    import PanelCategoryItems from './PanelCategoryItems.vue';
    import SlidePanel from '../../../../library/SlidePanel.vue';
    import ActivationBriefProductConfigMixin from '../../mixins/ActivationBriefProductConfigMixin';
    import ActivationBriefProductsMixin from '../../../../providers/mixins/ActivationBriefProductsMixin';
    import { INFLUENCER_CHOICE_VALUE } from '../../../../../constants/activation-products';
    import { useFeatureFlagsStore } from '../../../../../../stores';

    export default {
        name: 'ActivationBriefProductsPanel',
        components: {
            SingleProductPanelItem,
            NewSingleProductPanelItem,
            SlidePanel,
            PanelProductItems,
            PanelCategoryItems
        },
        mixins: [ActivationBriefProductConfigMixin, ActivationBriefProductsMixin],
        props: {
            visibility: {
                type: Boolean,
                required: true
            },
            editingProduct: {
                type: Object,
                default: null
            },
            currentSelection: {
                type: Object,
                default: () => ({})
            },
            panelSlot: {
                type: Object,
                default: () => ({})
            }
        },
        watch: {
            visibility: {
                immediate: true,
                handler(val, oldVal) {
                    const justAppeared = val && !oldVal;

                    if (!justAppeared) {
                        return;
                    }

                    if (this.editingProduct) {
                        this.toSingle(this.editingProduct);
                    } else {
                        this.startPanel();
                    }
                }
            }
        },
        data() {
            return {
                productsList: [],
                categoryList: [],
                categoryListVisibility: false,
                productListVisibility: false,
                singleItemVisibility: false,
                selectedItem: {},
                isPanelLoading: false,
                page: 1,
                showLoadMore: false,
                isLoading: false,
                currentOptions: [],
                loadMoreUrl: null
            };
        },
        computed: {
            isVisible() {
                return !!(this.categoryListVisibility || this.productListVisibility || this.singleItemVisibility);
            },
            useNewVariantsSelection() {
                const flags = useFeatureFlagsStore().data;
                return flags['product-variants-selection-in-live-site'] || false;
            }
        },
        methods: {
            toCategory(category) {
                if (category.category.is_nav) {
                    this.loadMoreUrl = `/api/v1/product-categories/${category.category_id}/categories`;
                } else {
                    this.loadMoreUrl = `/api/v1/product-categories/${category.category_id}/products`;
                }
                this.isPanelLoading = true;
                this.$http.get(this.loadMoreUrl).then((resp) => {
                    this.toProducts(resp.body.data);
                    this.handleShowLoadMoreButton(resp.body.meta.total);
                    this.isPanelLoading = false;
                    this.showLoadMore = resp.body.data.length < resp.body.meta.total;
                    this.page = 2;
                });
            },
            toProducts(products) {
                this.categoryListVisibility = false;
                this.productListVisibility = true;
                this.singleItemVisibility = false;

                this.productsList = products;
            },
            resetPanels() {
                this.categoryListVisibility = false;
                this.productListVisibility = false;
                this.singleItemVisibility = false;
            },
            toSingle(product, productIdx) {
                this.isPanelLoading = true;
                this.getProducts(product.id).then((products) => {
                    this.resetPanels();

                    this.selectedItem = {
                        ...products[0],
                        ...product
                    };
                    const slot = this.panelSlot && this.panelSlot.id;
                    const foundConfig = this.getProductConfigItem('product_id', product.id, slot, productIdx);
                    this.currentOptions =
                        foundConfig && foundConfig.product_option_values
                            ? foundConfig.product_option_values
                                  .filter((optValue) => optValue.value !== INFLUENCER_CHOICE_VALUE.value)
                                  .map((optValue) => {
                                      return {
                                          id: optValue.option_id,
                                          value_id: optValue.value,
                                          name: optValue.label
                                      };
                                  })
                            : get(this.selectedItem, 'variants.0.options', []);
                    this.singleItemVisibility = true;
                    this.isPanelLoading = false;
                });
            },
            loadMoreProducts() {
                this.isLoading = true;
                this.loadMoreUrl = this.loadMoreUrl || `/api/v1/activations/${this.brief.activation.id}/products`;
                this.$http
                    .get(`${this.loadMoreUrl}?page=${this.page}`)
                    .then((resp) => {
                        this.productsList = this.productsList.concat(resp.body.data);
                        this.productListVisibility = true;
                        this.handleShowLoadMoreButton(resp.body.meta.total);
                        this.page++;
                    })
                    .finally(() => (this.isLoading = false));
            },
            handleShowLoadMoreButton(totalProducts) {
                this.showLoadMore = this.productsList.length < totalProducts;
            },
            findProductItems(items) {
                if (isEmpty(items)) {
                    this.productListVisibility = true;
                    return Promise.resolve();
                }

                this.isPanelLoading = true;
                const preselectedProductIds = items.map((productConfigItem) => productConfigItem.product_id);

                this.getProducts(preselectedProductIds).then((products) => {
                    this.productsList = products.map((item) => {
                        const configItem = items.find((confItem) => confItem.product_id === item.id);

                        if (!configItem) {
                            return item;
                        }

                        for (const optionIdx in configItem.product_option_values) {
                            const optionConf = configItem.product_option_values[optionIdx];
                            if (optionConf.value === INFLUENCER_CHOICE_VALUE.value) {
                                // Let creator decide
                                continue;
                            }

                            const option = item.options.find((itemOpt) => itemOpt.id === optionConf.option_id);
                            if (!option) {
                                continue;
                            }

                            option.values = option.values.filter((opt) => opt.id === optionConf.value);
                        }

                        return item;
                    });
                    this.productListVisibility = true;
                    this.isPanelLoading = false;
                });
            },
            startPanel() {
                const allNonSlotCondition =
                    (this.selectionMethodUnlimited || this.selectionMethodLimitAmount) && this.limitationMethodNone;
                const allSlotCondition = this.panelSlot && this.panelSlot.limitation_method === 'none';

                if (allNonSlotCondition || allSlotCondition) {
                    this.resetPanels();
                    this.loadMoreProducts();
                } else if (
                    (this.selectionMethodUnlimited || this.selectionMethodLimitAmount) &&
                    this.limitationMethodProducts
                ) {
                    this.findProductItems(this.productConfig.items);
                } else if (
                    (this.selectionMethodUnlimited || this.selectionMethodLimitAmount) &&
                    this.limitationMethodCategories
                ) {
                    this.categoryList = this.productConfig.items;
                    this.categoryListVisibility = true;
                } else if (this.selectionMethodLimitAmount && this.limitationMethodSlots) {
                    this.resetPanels();

                    if (this.panelSlot.limitation_method === 'categories') {
                        this.categoryList = this.panelSlot.items;
                        this.categoryListVisibility = true;
                    } else if (this.panelSlot.limitation_method === 'products') {
                        this.findProductItems(this.panelSlot.items);
                        this.productListVisibility = true;
                        this.categoryList = [];
                    }
                }
            },
            handleBackButton() {
                if (this.singleItemVisibility && !this.editingProduct) {
                    this.productListVisibility = true;
                    this.singleItemVisibility = false;
                    return;
                }
                if (this.productListVisibility && this.categoryList.length > 0) {
                    this.productListVisibility = false;
                    this.categoryListVisibility = true;
                    return;
                }
                this.$emit('close');
            }
        }
    };
</script>

<style lang="scss" scoped>
    .products-load-more-spinner {
        margin-bottom: 25px;
        margin-top: 10px;
        display: block;
        width: 100%;
    }
</style>
